import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import {
  PatientApiService,
  Patient,
  PATIENT_ACCESS_KEY,
  PATIENT_REFRESH_KEY,
} from '../services/patientApi';

interface LoginResult {
  isNew: boolean;
}

interface PatientAuthContextType {
  patient: Patient | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithGoogle: (accessToken: string) => Promise<LoginResult>;
  requestCode: (email: string) => Promise<void>;
  loginWithCode: (email: string, code: string) => Promise<LoginResult>;
  refreshPatient: () => Promise<void>;
  logout: () => void;
}

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(undefined);

export const PatientAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const restore = async () => {
      if (localStorage.getItem(PATIENT_ACCESS_KEY)) {
        try {
          setPatient(await PatientApiService.getMe());
        } catch {
          localStorage.removeItem(PATIENT_ACCESS_KEY);
          localStorage.removeItem(PATIENT_REFRESH_KEY);
        }
      }
      setIsLoading(false);
    };
    restore();
  }, []);

  const storeSession = (data: {
    access: string;
    refresh: string;
    patient: Patient;
    is_new: boolean;
  }): LoginResult => {
    // Вход пациента гасит сессию персонала: одновременно в браузере живёт
    // РОВНО ОДНА личность. Интерцептор api.ts предпочитает staff-токен
    // пациентскому, поэтому сотрудник, вошедший затем как пациент,
    // продолжал ходить в API врачом — и получал в кабинете пациента
    // протокол консультации, подписывать который вправе только врач.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    localStorage.setItem(PATIENT_ACCESS_KEY, data.access);
    localStorage.setItem(PATIENT_REFRESH_KEY, data.refresh);
    setPatient(data.patient);
    return { isNew: data.is_new };
  };

  const loginWithGoogle = async (accessToken: string) =>
    storeSession(await PatientApiService.googleLogin(accessToken));

  const requestCode = async (email: string) => {
    await PatientApiService.requestCode(email);
  };

  const loginWithCode = async (email: string, code: string) =>
    storeSession(await PatientApiService.verifyCode(email, code));

  const refreshPatient = async () => {
    setPatient(await PatientApiService.getMe());
  };

  const logout = () => {
    localStorage.removeItem(PATIENT_ACCESS_KEY);
    localStorage.removeItem(PATIENT_REFRESH_KEY);
    setPatient(null);
  };

  return (
    <PatientAuthContext.Provider
      value={{
        patient,
        isAuthenticated: patient !== null,
        isLoading,
        loginWithGoogle,
        requestCode,
        loginWithCode,
        refreshPatient,
        logout,
      }}
    >
      {children}
    </PatientAuthContext.Provider>
  );
};

export const usePatientAuth = () => {
  const context = useContext(PatientAuthContext);
  if (context === undefined) {
    throw new Error('usePatientAuth must be used within PatientAuthProvider');
  }
  return context;
};
